"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Listbox = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _ControlledListbox = require("./ControlledListbox");
var _excluded = ["defaultValue", "defaultActiveValue", "onOptionFocus", "onChange"];
/**
 * A listbox is a control that allows you to select one or multiple values from a list of options.
 * It has a similar purpose to a `Combobox` or
 * `DropdownSelect` with the difference that it
 * doesn't conceal the options behind a dialog. You can read more about
 * the differences, pros/cons, and usage guidelines in this useful
 * [article from the Nielsen Norman Group](https://www.nngroup.com/articles/listbox-dropdown/).
 *
 * Listboxes use typeahead to quickly navigate to options. Typing characters in quick succession
 * will move the focus to the next option that matches the typed character(s).
 *
 * @related ['Combobox', 'DropdownSelect']
 * @subcomponents ['ListboxGroup', 'ListboxGroupLabel', 'ListboxOption']
 * @since 8.28.0
 */
var Listbox = /*#__PURE__*/(0, _react.forwardRef)(function (props, forwardedRef) {
  var defaultValue = props.defaultValue,
    defaultActiveValue = props.defaultActiveValue,
    onOptionFocus = props.onOptionFocus,
    onChange = props.onChange,
    rest = (0, _objectWithoutProperties2.default)(props, _excluded);
  var _useState = (0, _react.useState)(defaultValue),
    _useState2 = (0, _slicedToArray2.default)(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var _useState3 = (0, _react.useState)(defaultActiveValue),
    _useState4 = (0, _slicedToArray2.default)(_useState3, 2),
    activeValue = _useState4[0],
    setActiveValue = _useState4[1];
  var onOptionFocusCallback = (0, _react.useCallback)(function (option, meta) {
    setActiveValue(option && option.value);
    if (typeof onOptionFocus === 'function') {
      onOptionFocus(option, meta);
    }
  }, [onOptionFocus]);
  var onChangeCallback = (0, _react.useCallback)(function (nextValue, meta) {
    setValue(nextValue);
    if (typeof onChange === 'function') {
      onChange(nextValue, meta);
    }
  }, [onChange]);
  return /*#__PURE__*/_react.default.createElement(_ControlledListbox.ControlledListbox, (0, _extends2.default)({
    activeValue: activeValue,
    onOptionFocus: onOptionFocusCallback,
    onChange: onChangeCallback,
    ref: forwardedRef,
    value: value
  }, rest));
});
exports.Listbox = Listbox;
Listbox.displayName = 'Listbox';
Listbox.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The value that currently has focus when used as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * **Note:** When `selectOnFocus` is enabled, the active option will automatically be
   * set to the selected value if this is omitted.
   */
  activeValue: _propTypes.default.string,
  /**
   * An optional appearance for listbox options. This will default to `'checkbox'` for
   * multiselectable lists. Icons will take precedence over this when defined on individual list
   * options.
   */
  appearance: _propTypes.default.oneOf(['checkbox', 'radio']),
  /**
   * The `activeValue` when used as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  defaultActiveValue: _propTypes.default.string,
  /**
   * The selected option(s) when used as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * This should be a string for a single-select listbox, and an array of strings for a
   * multiselectable listbox.
   */
  defaultValue: _propTypes.default.oneOfType([_propTypes.default.arrayOf(_propTypes.default.string), _propTypes.default.string]),
  /**
   * Options will be focused automatically when they are hovered.
   *
   * Note: This should be avoided when using `selectOnFocus`.
   */
  focusOnHover: _propTypes.default.bool,
  /**
   * A function that maps the values passed to `options` to an object of props that will be
   * passed to `renderOption`.
   *
   * By default, `mapOptionsToProps` will convert strings into an object where the string is both
   * the `label` and `value`. If `label` or `value` is omitted from an object, the other will be
   * used. If no `id` is provided, one will automatically be generated.
   */
  mapOptionToProps: _propTypes.default.func,
  /**
   * Called whenever the selected value(s) changes with the new selected value(s) and a `meta`
   * object as parameters.
   *
   * - `value` will be the selected option value or values when multiselectable
   * - `meta` will have `meta.reason` detailing why the change occurred and `meta.option` as the
   *     last `option` object to change
   *
   * Note: This differs from `onOptionSelect` in that it only returns the value(s) and not the
   * option object(s).
   */
  onChange: _propTypes.default.func,
  /**
   * Called whenever an option is focused with `option` and `meta` objects as parameters.
   *
   * - `option` will be the object returned from `mapOptionToProps`
   * - `meta` will have a `meta.reason` detailing why the change occurred
   */
  onOptionFocus: _propTypes.default.func,
  /**
   * Called whenever an option is selected with `option` and `meta` objects as parameters.
   *
   * - `option` will be the object returned from `mapOptionToProps`
   * - `meta` will have a `meta.reason` detailing why the change occurred
   *
   * By default, this will only be called when the action results in a change of value.
   * You can use `selectCallbackFrequency` to change this behavior to always trigger.
   */
  onOptionSelect: _propTypes.default.func,
  /**
   * An array of options, or an array of optgroups. An optgroup is an array of options, where the
   * first option is the header for the group.
   *
   * Option values will be passed to `mapOptionToProps` before being passed to the `renderOption`
   * callback.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  options: _propTypes.default.array,
  /**
   * A function that receives `props` as the only argument and returns an option group component,
   * by default a `ListboxGroup`.
   */
  renderGroup: _propTypes.default.func,
  /**
   * A function that receives `props` as the only argument and returns an option group label
   * component, by default a `ListboxGroupLabel`.
   */
  renderGroupLabel: _propTypes.default.func,
  /**
   * A function that receives `props` as the only argument and returns an option component, by
   * default a `ListboxOption`.
   */
  renderOption: _propTypes.default.func,
  /**
   * The ARIA role of the component. This should not be changed from the default
   * `Listbox`.
   */
  role: _propTypes.default.string,
  /**
   * By default, `onOptionSelect` will only fire if the action results in a change of value.
   * Alternatively, you can set this to `CALLBACK_FREQUENCY.ALWAYS` and the callback will
   * always fire regardless of the outcome on the value.
   *
   * Note: When `CALLBACK_FREQUENCY.ALWAYS` is enabled, you can check if the value will
   * change by looking at the `onOptionSelect` `meta.valueChanged` property.
   */
  selectCallbackFrequency: _propTypes.default.oneOf(Object.values(_constants.CALLBACK_FREQUENCY)),
  /**
   * When true, focus will also trigger a select. This will always be `false` for a
   * multiselectable Listbox.
   *
   * This model of selection is known as "selection follows focus". Having selection follow focus
   * can be very helpful in some circumstances and can severely degrade accessibility in others.
   * For additional guidance, see
   * [Deciding When to Make Selection Automatically Follow Focus](https://www.w3.org/TR/wai-aria-practices-1.2/#kbd_selection_follows_focus).
   */
  selectOnFocus: _propTypes.default.bool,
  /**
   * By default, only the space key will toggle selection. Set this to `true` to allow the enter
   * key to select.
   */
  selectWithEnter: _propTypes.default.bool,
  /**
   * Space key will toggle selection by default. Set this to `false` to disable.
   */
  selectWithSpace: _propTypes.default.bool,
  /**
   * Listboxes have a fixed height by default. You can set this to `"fluid"` to match the height
   * of the parent container.
   */
  size: _propTypes.default.oneOf([_constants.INPUT_SIZES.md, 'fluid']),
  /**
   * Typing characters in quick succession will move the focus to the next option that matches the
   * typed character(s).
   *
   * This is the delay in milliseconds between keystrokes after which the typeahead input will
   * reset and start fousing a different option.
   */
  typeaheadInputDelay: _propTypes.default.number,
  /**
   * The selected option(s) when used as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * This should be a string for a single-select listbox, and an array of strings for a
   * multiselectable listbox.
   */
  value: _propTypes.default.oneOfType([_propTypes.default.arrayOf(_propTypes.default.string), _propTypes.default.string])
} : {};
Listbox.defaultProps = {
  defaultValue: ''
};